import React from 'react';

// MUI
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Accordian from '@mui/material/Accordion'; // Note this is updated to Accordion in current MUI
import AccordianSummaryBase from '@mui/material/AccordionSummary';
import AccordianDetails from '@mui/material/AccordionDetails';
import { humanizeSnake } from 'lib/v1/format';

// PageElements
import SidebarHeader from 'components/v2/PageElements/Sidebar/Header';
import SidebarList from 'components/v2/PageElements/Sidebar/List';
import SidebarListItem from 'components/v2/PageElements/Sidebar/ListItem'; // components
import Avatar from 'components/v2/PageElements/Avatar';

// FormElements
import Button from 'components/v2/FormElements/Button';

// Styled Components
const DeltaContainer = styled(Box)({
  fontSize: '0.875rem',
  wordBreak: 'break-word',
  paddingBottom: '0.25rem',
});

const HistoryItemContainer = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  padding: '0.75rem 0',
});

const StyledAvatar = styled(ListItemAvatar)({
  marginTop: 0,
  marginRight: '1rem',
  minWidth: 'auto',
});

const Action = styled('span')({
  fontWeight: 400,
  marginLeft: '0.25rem',
});

const Author = styled('span')({
  fontWeight: '600',
  whiteSpace: 'nowrap',
});

const StyledAccordion = styled(Accordian)({
  padding: 0,
  margin: 0,
  backgroundColor: 'inherit',
});

const AccordianSummary = styled(AccordianSummaryBase)({
  padding: 0,
  margin: 0,
  '&.Mui-disabled': {
    padding: 0,
    margin: 0,
  },
  '& .MuiAccordionSummary-content': {
    padding: 0,
    margin: 0,
  },
});

const StyledAccordionDetails = styled(AccordianDetails)({
  padding: 0,
  margin: 0,
  backgroundColor: 'inherit',
});

const Delta = styled('span')({
  fontWeight: '700',
  whiteSpace: 'nowrap',
  paddingRight: '0.25rem',
  marginTop: '1.0rem',
  paddingLeft: '1.0rem',
});

const DateText = styled('span')(({ theme }) => ({
  fontWeight: '400',
  color: theme.palette.text.secondary,
  fontSize: '0.8125rem',
  whiteSpace: 'nowrap',
}));

const HistoryDelta = ({ field, previous, current }) => {
  const actionName = previous
    ? ` changed from ${previous} to ${current ? current : 'none'}`
    : ` set to ${current}`;
  return (
    <DeltaContainer>
      <Delta>{humanizeSnake(field)}</Delta>
      <Action>{actionName}</Action>
    </DeltaContainer>
  );
};

export const ExpandableHistoryItem = ({ user, name, action, date, object_changes = {} }) => {
  const deltas = Object.entries(object_changes);

  return (
    <SidebarListItem>
      <StyledAccordion elevation={0}>
        <AccordianSummary>
          <HistoryItemContainer>
            <StyledAvatar>
              <Avatar lg profile={user} />
            </StyledAvatar>
            <ListItemText>
              <Box flex={1}>
                <DeltaContainer>
                  <Author>{name}</Author>
                  <Action>{action}</Action>
                </DeltaContainer>
                <DeltaContainer>
                  <DateText>{date}</DateText>
                </DeltaContainer>
              </Box>
            </ListItemText>
          </HistoryItemContainer>
        </AccordianSummary>
        <StyledAccordionDetails>
          <Box>
            {deltas.map(([key, val], index) => (
              <HistoryDelta
                field={key}
                previous={val[0]}
                current={val[1]}
                key={`history-delta-${index}`}
              />
            ))}
          </Box>
        </StyledAccordionDetails>
      </StyledAccordion>
    </SidebarListItem>
  );
};

export const HistoryItem = ({ user, name, action, date, object_changes = {} }) => {
  return (
    <SidebarListItem>
      <HistoryItemContainer>
        <StyledAvatar>
          <Avatar lg profile={user} />
        </StyledAvatar>
        <Box flex={1}>
          <DeltaContainer>
            <Author>{name}</Author>
            <Action>{action}</Action>
          </DeltaContainer>
          <DeltaContainer>
            <DateText>{date}</DateText>
          </DeltaContainer>
        </Box>
      </HistoryItemContainer>
    </SidebarListItem>
  );
};

export const HistoryList = ({ data = [], showAll = false, limit = 5 }) => {
  if (!data || !Array.isArray(data)) return;

  const visibleData = showAll ? data : data.slice(0, limit);

  return (
    <SidebarList>
      {visibleData.map((props, index) => {
        const hasDelta =
          props && props.object_changes && Object.entries(props.object_changes).length === 0
            ? false
            : true;
        return hasDelta ? (
          <ExpandableHistoryItem key={`expandable-history-item-${index}`} {...props} />
        ) : (
          <HistoryItem key={`history-item-${index}`} {...props} />
        );
      })}
    </SidebarList>
  );
};

const History = ({ data = [], limit = 5, ...props }) => {
  const [showAll, setShowAll] = React.useState(false);
  const arrayLength = Array.isArray(data) && data.length;
  const showButton = arrayLength > limit;

  const historyProps = {
    ...props,
    data,
    limit,
    showAll,
  };

  return (
    <div className="historyModule">
      <SidebarHeader>History</SidebarHeader>
      <HistoryList {...historyProps} />
      {showButton && (
        <Button
          component="span"
          color="primary"
          variant="text"
          disableRipple
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? `Only ${limit} newest activities` : `All ${arrayLength} activities`}
        </Button>
      )}
    </div>
  );
};

export default History;
